import React, { useState } from 'react';
import { Card, Typography, Tag, Button, Row, Col } from 'antd';
import {
  CrownOutlined,
  EyeOutlined,
  CheckCircleFilled,
  LoginOutlined,
} from '@ant-design/icons';
import { ROLE_ADMIN, ROLE_USER } from '../roles';
import useRbac from './useRbac';

const { Title, Text, Paragraph } = Typography;

interface RoleCardConfig {
  label: string;
  color: string;
  tagColor: string;
  icon: React.ReactNode;
  description: string;
  highlights: string[];
}

const ROLE_CARDS: Record<string, RoleCardConfig> = {
  [ROLE_ADMIN]: {
    label: 'Admin',
    color: '#ff4d4f',
    tagColor: 'red',
    icon: <CrownOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />,
    description: 'Full read and write access to all features.',
    highlights: [
      'Create new products & SKUs',
      'Update all entities & fields',
      'Manage entitlements & configs',
      'View all data',
    ],
  },
  [ROLE_USER]: {
    label: 'User',
    color: '#1890ff',
    tagColor: 'blue',
    icon: <EyeOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
    description: 'Limited write access with restricted field editing.',
    highlights: [
      'Update selected entities',
      'View all data',
      'Restricted field editing',
      'No product/SKU onboarding',
    ],
  },
};

/**
 * Full-page role selection screen shown after login when the
 * user has multiple Okta roles assigned. Similar to the AWS
 * account/role selection experience.
 */
const RoleSelector: React.FC = () => {
  const { availableRoles, switchRole } = useRbac();
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const handleContinue = () => {
    if (selectedRole) {
      switchRole(selectedRole);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 700, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Title level={2} style={{ color: '#fff', marginBottom: 8 }}>
            Select Your Role
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16 }}>
            Your account has access to multiple roles. Choose how you&apos;d like to proceed.
          </Text>
        </div>

        <Row gutter={[24, 24]} justify="center">
          {availableRoles.map((r) => {
            const config = ROLE_CARDS[r];
            if (!config) { return null; }
            const isSelected = selectedRole === r;

            return (
              <Col key={r} xs={24} sm={12}>
                <Card
                  hoverable
                  onClick={() => setSelectedRole(r)}
                  style={{
                    borderRadius: 12,
                    border: isSelected
                      ? `2px solid ${config.color}`
                      : '2px solid transparent',
                    boxShadow: isSelected
                      ? `0 0 0 2px ${config.color}33, 0 8px 24px rgba(0,0,0,0.12)`
                      : '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                  bodyStyle={{ padding: 28 }}
                >
                  {isSelected && (
                    <CheckCircleFilled
                      style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        fontSize: 22,
                        color: config.color,
                      }}
                    />
                  )}

                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    {config.icon}
                    <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                      {config.label}
                    </Title>
                    <Tag color={config.tagColor}>{config.label.toUpperCase()}</Tag>
                  </div>

                  <Paragraph
                    type="secondary"
                    style={{ textAlign: 'center', marginBottom: 16 }}
                  >
                    {config.description}
                  </Paragraph>

                  <div>
                    {config.highlights.map((h, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 8,
                          fontSize: 13,
                          color: '#595959',
                        }}
                      >
                        <CheckCircleFilled
                          style={{ color: config.color, fontSize: 14, flexShrink: 0 }}
                        />
                        {h}
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Button
            type="primary"
            size="large"
            icon={<LoginOutlined />}
            disabled={!selectedRole}
            onClick={handleContinue}
            style={{
              height: 48,
              paddingLeft: 40,
              paddingRight: 40,
              borderRadius: 8,
              fontSize: 16,
            }}
          >
            Continue as{' '}
            {selectedRole && ROLE_CARDS[selectedRole]
              ? ROLE_CARDS[selectedRole].label
              : '...'}
          </Button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
            You can switch your role later from the header menu.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default RoleSelector;
